/**
 * Default About Us content — mirrors fallbacks in AboutUsPage.js (text only).
 * Used by CMS forms so fields are never empty on first load.
 */
export const ABOUT_US_DEFAULTS = {
  hero_title: 'About Us',
  hero_summary: 'Discover our story, values, and the people who shape Permata Harapan School.',
  hero_slides: [],

  principal_name: 'Dr. Amelia Hartono',
  principal_credentials: 'M.Ed., Ed.D.',
  principal_role: 'Principal / Head of School',
  principal_opening: 'Dear Parents, Students, and Members of the Permata Harapan Community,',
  principal_paragraph_one:
    'Welcome to Permata Harapan School. Our mission is to nurture young people of character, competence, and compassion. We believe meaningful education combines academic rigor, strong values, and authentic care for every learner.',
  principal_paragraph_two:
    'Together with families, we build a learning environment where students are encouraged to think critically, lead responsibly, and serve others with integrity. We are committed to preparing each child not only for examinations, but for life and purpose.',
  principal_closing:
    'Thank you for your trust and partnership. We look forward to walking this journey with you.',
  principal_theme_line: 'Nurturing Character, Inspiring Excellence, Building Futures.',
  principal_photo: '',

  welcome_kicker: 'Welcome Message',
  welcome_title: 'From the Principal / Head of School',
  welcome_subtitle: 'A message of vision, values, and partnership',

  values_kicker: 'Our Direction',
  values_title: 'Vision, Mission, and Core Values',
  values_intro:
    'These guiding principles shape how we teach, lead, and care for every student at Permata Harapan School.',
  vision_label: 'Vision',
  vision_title:
    'To be inspired by faith, to be strengthened with fortitude, and to be motivated to achieve fulfillment through lifelong learning',
  vision_description:
    'Our vision guides every decision we make, ensuring that faith, courage, and purpose are at the heart of our educational journey.',
  mission_label: 'Mission',
  mission_title:
    "PHB Secondary School is dedicated to empower students through a values-driven education, nurturing each student's spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.",
  mission_description:
    'We empower every student to become confident learners, compassionate leaders, and responsible global citizens through rigorous academics and character formation.',
  core_values_label: 'Core Values',
  core_values_title:
    "Sekolah Permata Harapan Secondary is dedicated to empower students through a values-driven education, nurturing each student's spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.",
  core_values_description:
    'Our core values of integrity, excellence, compassion, and service define who we are and guide how we treat one another every day.',

  school_philosophy: [
    {
      key: 'motto',
      label: 'School Motto',
      shortTitle: 'Non Scholae Sed Vitae Discimus',
      title: 'Non Scholae Sed Vitae Discimus',
      description:
        'The true purpose of education extends beyond academic achievement or the completion of schooling it is to cultivate the knowledge, skills, and character essential for meaningful success in life.',
      quote: 'Non Scholae Sed Vitae Discimus',
      index: '01',
      image: '',
    },
    {
      key: 'philosophy',
      label: 'Philosophy',
      shortTitle: 'Faith, Fortitude, Fulfilment',
      title: 'Faith, Fortitude, and Fulfilment',
      points: [
        {
          title: 'Faith',
          description:
            'Faith serves as the foundational pillar of student character and development. It cultivates core values that shape how students think, act, and relate to others.',
        },
        {
          title: 'Fortitude',
          description:
            'Fortitude embodies the strength, courage, and perseverance required to face and overcome adversity.',
        },
        {
          title: 'Fulfilment',
          description:
            'Fulfilment represents the sense of purpose and accomplishment that comes from personal growth and meaningful service to others.',
        },
      ],
      quote:
        'Together, Faith, Fortitude, and Fulfilment reflect the holistic qualities that Permata Harapan Secondary seeks to develop in every student.',
      index: '02',
      image: '',
    },
    {
      key: 'logo',
      label: 'Logo Philosophy',
      shortTitle: 'Five Elements of Excellence',
      title: 'Logo Philosophy',
      points: [
        { title: 'Five-Leaved Tree', description: "A symbol of growth at Permata Harapan Secondary School." },
        { title: 'Two Students in Graduation Gowns', description: 'Represent Permata Harapan students on their journey to success.' },
        { title: 'Star', description: 'A symbol that inspires Permata Harapan students to dream big and aim high.' },
        { title: 'Circle', description: 'Represents balance, harmony, and unity in the learning environment.' },
        { title: 'Ribbon', description: "A symbol of honor and celebration." },
      ],
      index: '03',
      image: '',
    },
  ],

  history_timeline: [
    { year: '2012', title: 'Primary Campus Opens', description: 'Permata Harapan School opens its first primary campus with a focus on faith, values, and academic excellence.', image: '' },
    { year: '2021', title: 'Elementary Programme Launch', description: 'The school expands to serve growing families with a strengthened elementary learning pathway.', image: '' },
    { year: '2026', title: 'Junior High Programme', description: 'SMP opens with enriched academics and leadership formation for early adolescents.', image: '' },
    { year: '2027', title: 'Senior High Programme', description: 'SMA launches to prepare students for university pathways and global opportunities.', image: '' },
  ],

  leadership_profiles: [
    { name: 'Dr. Amelia Hartono', role: 'Principal / Head of School', bio: 'Leads the school vision, learning culture, and long-term academic growth with a focus on faith-centered excellence.', photo: '' },
    { name: 'Mrs. Clara Wijaya', role: 'Vice Principal, Academic Affairs', bio: 'Oversees curriculum quality, teacher development, and student achievement across every grade level.', photo: '' },
    { name: 'Mr. Daniel Prasetyo', role: 'Head of Student Life', bio: 'Guides character formation, leadership programs, and student well-being initiatives.', photo: '' },
    { name: 'Mrs. Rina Santoso', role: 'Head of Operations', bio: 'Ensures daily operations, campus safety, and family services run smoothly for the school community.', photo: '' },
  ],

  why_started: {
    why_started_headline: 'A promise to families, built for the next generation.',
    why_started_paragraph: 'We began with a simple mission: create a safe, joyful place where learning feels meaningful and character grows alongside achievement.',
    why_started_tags: ['Faith-led foundation', 'Future-ready learning', 'Whole-child care', 'Family partnership'],
    why_started_note: 'Growing with our community since 2012.',
  },

  established: {
    established_date: 'July 2003',
    established_summary: 'Built from a parent-led vision to raise confident learners with character, curiosity, and joyful service.',
    established_tags: ['Primary', 'Values-Driven', 'Community', 'Faith-Centered'],
    established_facts: [
      { label: 'Focus', value: 'Faith-led growth' },
      { label: 'Approach', value: 'Whole-child learning' },
      { label: 'Impact', value: 'Community-first' },
    ],
    established_cta: 'Built on faith, growing with families.',
  },
};

const ARRAY_FIELDS = ['school_philosophy', 'history_timeline', 'leadership_profiles', 'hero_slides'];

export function mergeAboutUsData(dbData = {}) {
  const merged = { ...ABOUT_US_DEFAULTS, ...dbData };
  ARRAY_FIELDS.forEach((field) => {
    if (!Array.isArray(dbData[field]) || dbData[field].length === 0) {
      merged[field] = ABOUT_US_DEFAULTS[field];
    }
  });
  return merged;
}